import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Circle, Path, Line, Text } from 'fabric';
import {
  calculateDistance,
  isNearPoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
} from './BezierTool';
import { centerlineToPathData } from './wegCenterline';
import { createWegPath, resolveWegStyle } from './wegPath';
import { useCommand } from '../../../context/CommandContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { CreateObjectCommand } from '../../../commands/CreateObjectCommand';
import { useTouchDeferredVertex } from './useTouchDeferredVertex';
import DrawingToolActions from './DrawingToolActions';

/**
 * Centerline Drawing Tool (#720)
 *
 * Zeichnet einen Weg als **offene** Mittellinie: Punkte klicken, mit Enter oder
 * Doppelklick abschließen (kein Auto-Schließen — ein Weg ist keine Fläche). Der
 * Verlauf (kurvig/eckig) kommt aus dem Config-Default und ist danach im Panel
 * umschaltbar. Das fertige Objekt ist ein Stroke-Ribbon (createWegPath).
 *
 * Interaktion und Snapping spiegeln den Bezier-Zeichner; die Unterschiede sind
 * bewusst: MIN = 2 Punkte, offener Abschluss, Ribbon statt geschlossener Kurve.
 */
const CenterlineDrawingTool = ({
  canvas,
  gridSize,
  snapEnabled,
  selectedObjectType,
  onComplete,
  onCancel,
}) => {
  const [controlPoints, setControlPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mousePos, setMousePos] = useState(null);
  const [mousePosScreen, setMousePosScreen] = useState(null);
  const [ctrlPressed, setCtrlPressed] = useState(false);

  const { commandStack } = useCommand();
  const { settings } = useGardenSettings();

  // Ref-Spiegel der Punkte: dblclick/keydown lesen den aktuellen Stand synchron.
  const controlPointsRef = useRef([]);
  useEffect(() => {
    controlPointsRef.current = controlPoints;
  }, [controlPoints]);

  const controlPointMarkersRef = useRef([]);
  const edgeLabelsRef = useRef([]);
  const previewRef = useRef(null);
  const snapIndicatorRef = useRef(null);

  const CONTROL_POINT_SIZE = 6;
  const FIRST_POINT_SIZE = 8;
  const MIN_CONTROL_POINTS = 2; // Ein Weg braucht mindestens Start + Ende
  const DEDUP_PROXIMITY = 4; // dblclick fügt zwei Downs am selben Punkt → einen droppen

  const pixelsPerMeter = settings?.backgroundImage?.calibration?.scale || null;
  const { verlauf: defaultVerlauf, width: defaultWidth, color: defaultColor } =
    resolveWegStyle(selectedObjectType);

  const formatDistance = useCallback((distanceInPixels) => {
    if (pixelsPerMeter && pixelsPerMeter > 0) {
      return `${(distanceInPixels / pixelsPerMeter).toFixed(2)}m`;
    }
    return `${distanceInPixels.toFixed(0)}cm`;
  }, [pixelsPerMeter]);

  const removeSnapIndicator = useCallback(() => {
    if (snapIndicatorRef.current) {
      canvas?.remove(snapIndicatorRef.current);
      snapIndicatorRef.current = null;
    }
  }, [canvas]);

  const clearVisuals = useCallback(() => {
    if (!canvas) return;
    controlPointMarkersRef.current.forEach(m => canvas.remove(m));
    edgeLabelsRef.current.forEach(l => canvas.remove(l));
    if (previewRef.current) {
      canvas.remove(previewRef.current);
      previewRef.current = null;
    }
    removeSnapIndicator();
    controlPointMarkersRef.current = [];
    edgeLabelsRef.current = [];
    // Sicherheitsnetz: alles noch als temporär Markierte entfernen (#682).
    canvas.getObjects().filter(obj => obj.data?.isTemporary).forEach(obj => canvas.remove(obj));
    canvas.requestRenderAll();
  }, [canvas, removeSnapIndicator]);

  const renderVertices = useCallback(() => {
    controlPointMarkersRef.current.forEach(m => canvas?.remove(m));
    controlPointMarkersRef.current = [];
    controlPoints.forEach((point, index) => {
      const isFirst = index === 0;
      const marker = new Circle({
        left: point.x,
        top: point.y,
        radius: isFirst ? FIRST_POINT_SIZE : CONTROL_POINT_SIZE,
        fill: isFirst ? '#10B981' : '#FFFFFF',
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        originX: 'center',
        originY: 'center',
        data: { isTemporary: true },
      });
      canvas?.add(marker);
      controlPointMarkersRef.current.push(marker);
    });
  }, [canvas, controlPoints]);

  const renderEdges = useCallback(() => {
    if (!canvas) return;
    edgeLabelsRef.current.forEach(l => canvas.remove(l));
    edgeLabelsRef.current = [];
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const start = controlPoints[i];
      const end = controlPoints[i + 1];
      const label = new Text(formatDistance(calculateDistance(start, end)), {
        left: (start.x + end.x) / 2,
        top: (start.y + end.y) / 2,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        selectable: false,
        evented: false,
        originX: 'center',
        originY: 'center',
        data: { isTemporary: true, isMeasurementLabel: true },
      });
      canvas.add(label);
      edgeLabelsRef.current.push(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, controlPoints]);

  const renderPreview = useCallback(() => {
    if (!canvas) return;
    if (previewRef.current) {
      canvas.remove(previewRef.current);
      previewRef.current = null;
    }
    if (!mousePos || controlPoints.length < 1) return;

    const previewPoints = [...controlPoints, mousePos];
    if (previewPoints.length >= 2) {
      const pathData = centerlineToPathData(previewPoints, defaultVerlauf);
      if (!pathData) return;
      // Ribbon-Vorschau in halber Deckung, damit die Breite sichtbar ist.
      previewRef.current = new Path(pathData, {
        originX: 'left',
        originY: 'top',
        stroke: defaultColor,
        strokeWidth: defaultWidth,
        strokeLineCap: 'butt',
        strokeLineJoin: defaultVerlauf === 'eckig' ? 'miter' : 'round',
        fill: null,
        opacity: 0.35,
        selectable: false,
        evented: false,
        data: { isTemporary: true },
      });
      canvas.add(previewRef.current);
    } else {
      const last = controlPoints[controlPoints.length - 1];
      previewRef.current = new Line([last.x, last.y, mousePos.x, mousePos.y], {
        originX: 'left',
        originY: 'top',
        stroke: '#10B981',
        strokeWidth: 2,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
        data: { isTemporary: true },
      });
      canvas.add(previewRef.current);
    }
  }, [canvas, mousePos, controlPoints, defaultVerlauf, defaultWidth, defaultColor]);

  const renderSnapIndicator = useCallback((point, target) => {
    removeSnapIndicator();
    if (!target) return;
    const indicator = new Circle({
      left: point.x - 6,
      top: point.y - 6,
      originX: 'left',
      originY: 'top',
      radius: 6,
      fill: 'transparent',
      stroke: '#FFA500',
      strokeWidth: 2,
      selectable: false,
      evented: false,
      data: { isTemporary: true },
    });
    canvas.add(indicator);
    snapIndicatorRef.current = indicator;
  }, [canvas, removeSnapIndicator]);

  useEffect(() => {
    if (!canvas) return;
    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric render-batching flag; restored below
    canvas.renderOnAddRemove = false;
    renderVertices();
    renderEdges();
    renderPreview();
    canvas.renderOnAddRemove = true;
    canvas.renderAll();
  }, [canvas, renderVertices, renderEdges, renderPreview]);

  useEffect(() => {
    if (!canvas) return;
    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric cursor config; reverted in cleanup
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');
    return () => {
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
    };
  }, [canvas]);

  /** Finalisiert den Weg aus den gegebenen Punkten. */
  const finalize = useCallback((points) => {
    if (!points || points.length < MIN_CONTROL_POINTS) return;
    clearVisuals();
    const weg = createWegPath(points, {}, selectedObjectType);
    if (!weg) return;
    const command = new CreateObjectCommand(canvas, weg);
    commandStack.execute(command);
    setControlPoints([]);
    setIsDrawing(false);
    setMousePos(null);
    setMousePosScreen(null);
    onComplete?.(weg);
  }, [canvas, clearVisuals, commandStack, onComplete, selectedObjectType]);

  // Commit a point at the given (already snapped) location. Shared by the mouse
  // path (mouse:down) and the touch path (mouse:up, via useTouchDeferredVertex).
  const commitPoint = useCallback((snappedPoint) => {
    setControlPoints(prev => [...prev, snappedPoint]);
    setIsDrawing(true);
  }, []);

  // Clear the preview when a pinch cancels a pending touch point (#747).
  const clearTouchPreview = useCallback(() => setMousePos(null), []);
  const { deferIfTouch, updatePoint } = useTouchDeferredVertex(canvas, commitPoint, clearTouchPreview);

  useEffect(() => {
    if (!canvas) return;
    const handleMouseMove = (e) => {
      const pointer = canvas.getScenePoint(e.e);
      const snapResult = applyHierarchicalSnap(
        pointer, canvas, gridSize, snapEnabled, ctrlPressed, ENDPOINT_SNAP_DISTANCE
      );
      setMousePos(snapResult.point);
      updatePoint(snapResult.point); // #747: keep the touch commit-on-lift target current
      setMousePosScreen({ x: e.e.clientX, y: e.e.clientY });
      if (snapResult.snappedTo === 'endpoint') {
        renderSnapIndicator(snapResult.point, snapResult.target);
      } else {
        removeSnapIndicator();
      }
    };
    canvas.on('mouse:move', handleMouseMove);
    return () => canvas.off('mouse:move', handleMouseMove);
  }, [canvas, gridSize, snapEnabled, ctrlPressed, renderSnapIndicator, removeSnapIndicator, updatePoint]);

  useEffect(() => {
    if (!canvas) return;
    const handleMouseDown = (e) => {
      const pointer = canvas.getScenePoint(e.e);
      const snapResult = applyHierarchicalSnap(
        pointer, canvas, gridSize, snapEnabled, ctrlPressed, ENDPOINT_SNAP_DISTANCE
      );
      const snappedPoint = snapResult.point;
      // Touch: defer placement to lift so the point can be dragged into position
      // first — touch has no hover to preview before a tap (#747).
      if (deferIfTouch(e.e, snappedPoint)) {
        setMousePos(snappedPoint);
        return;
      }
      commitPoint(snappedPoint);
    };
    const handleDblClick = () => {
      // dblclick hängt zwei identische Downs an — trailing Duplikat entfernen.
      let points = [...controlPointsRef.current];
      if (
        points.length >= 2 &&
        isNearPoint(points[points.length - 1], points[points.length - 2], DEDUP_PROXIMITY)
      ) {
        points = points.slice(0, -1);
      }
      finalize(points);
    };
    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:dblclick', handleDblClick);
    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:dblclick', handleDblClick);
    };
  }, [canvas, gridSize, snapEnabled, ctrlPressed, finalize, commitPoint, deferIfTouch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && controlPointsRef.current.length >= MIN_CONTROL_POINTS) {
        finalize(controlPointsRef.current);
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape' && isDrawing) {
        clearVisuals();
        setControlPoints([]);
        setIsDrawing(false);
        setMousePos(null);
        setMousePosScreen(null);
        onCancel?.();
      }
      if (e.key === 'Control') setCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Control') setCtrlPressed(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing, clearVisuals, onCancel, finalize]);

  useEffect(() => {
    return () => clearVisuals();
  }, [clearVisuals]);

  const getInstructionText = () => {
    if (controlPoints.length < MIN_CONTROL_POINTS) {
      return `Punkt ${controlPoints.length + 1} • mindestens ${MIN_CONTROL_POINTS} Punkte`;
    }
    return `Punkt ${controlPoints.length + 1} • Enter oder Doppelklick beendet`;
  };

  // Touch: no Enter/Escape — cancel the whole in-progress path (#747).
  const handleTouchCancel = useCallback(() => {
    clearVisuals();
    setControlPoints([]);
    setIsDrawing(false);
    setMousePos(null);
    setMousePosScreen(null);
    onCancel?.();
  }, [clearVisuals, onCancel]);

  return (
    <>
      {isDrawing && mousePosScreen && (
        <div
          style={{
            position: 'fixed',
            left: `${mousePosScreen.x + 15}px`,
            top: `${mousePosScreen.y + 15}px`,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm border-2 border-emerald-500 rounded shadow-lg px-3 py-2">
            <div className="text-sm font-semibold text-gray-900 text-center">
              {getInstructionText()}
            </div>
            <div className="text-xs text-gray-500 text-center mt-1">
              {controlPoints.length} {controlPoints.length === 1 ? 'Punkt' : 'Punkte'}
            </div>
          </div>
        </div>
      )}
      <DrawingToolActions
        canFinish={controlPoints.length >= MIN_CONTROL_POINTS}
        onFinish={() => finalize(controlPointsRef.current)}
        onCancel={handleTouchCancel}
      />
    </>
  );
};

CenterlineDrawingTool.propTypes = {
  canvas: PropTypes.object.isRequired,
  gridSize: PropTypes.number.isRequired,
  snapEnabled: PropTypes.bool.isRequired,
  selectedObjectType: PropTypes.string,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

CenterlineDrawingTool.defaultProps = {
  selectedObjectType: null,
  onComplete: null,
  onCancel: null,
};

export default CenterlineDrawingTool;
